import React, {useEffect, useState} from 'react'
import {GridComponentProps} from '../../../core/_models'
import {GridPaginationComponent} from './GridPaginationComponent'
import {GridColumnComponent} from './GridColumnComponent'
import {ButtonComponent} from '../ButtonComponent'
import {useDynamicComponentsState} from '../../../core/DynamicComponentsState'
import {useThemeMode} from '../../../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import {PdfViewerGridButtonComponent} from './PdfViewerGridButtonComponent'
import {Button, Tooltip} from '@mui/material'
import axios from '../../../../../helpers/axios'
import {LoadingIndicator} from '../../../../common/components/LoadingIndicator'
import { Http } from "../../../../../helpers/http";

export function GridComponent({config, context}: GridComponentProps) {
  const {mode} = useThemeMode()
  const {setActivePage, getActivePage} = useDynamicComponentsState()
  const {
    id: gridId,
    data: initialData,
    export_url: exportUrl,
    view: {columns, noData, pager, controls = [], keyField},
    uuid,
    timeoutMs,
  } = config

  const [currentUpdateId, setCurrentUpdateId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(initialData)

  const columnsCount = columns.length + (controls.length > 0 ? 1 : 0)
  const totalEntries = data?.length ?? 0
  const hasPagination = data !== undefined && data.length > 0 && pager != null
  const pageSize = pager?.pageSize ?? 30

  const maxPages = Math.ceil(totalEntries / pageSize)
  const [page, setPage] = useState(Math.min(getActivePage(gridId) ?? 0, maxPages - 1))

  function handleChangePage(page: number) {
    setPage(page)
    setActivePage(gridId, page)
  }

  const loadForm = async () => {
    setLoading(true)
    try {
      const {
        data: {update_id},
      } = await Http.post<any>('get_cache_query_uid', {
        key: uuid,
      })
      if (update_id == null || currentUpdateId === update_id) return

      const {
        data: {data},
      } = await Http.post<any>('get_cache_query_data', {
        key: uuid,
      })

      setData(data)
      if( page < 0 ) handleChangePage(0)
      setCurrentUpdateId(update_id)
    } catch (e) {
    } finally {
      setLoading(false)
    }
  }

  const hasReload = uuid != null && timeoutMs != null

  let timer: ReturnType<typeof setTimeout> | null = null

  const startTimer = () => {
    if (hasReload) {
      if (timer != null) {
        clearTimeout(timer)
      }
      timer = null
      timer = setTimeout(
        () => {
          if (!loading) {
            loadForm()
          }
        },
        currentUpdateId == null ? 1000 : timeoutMs ?? 10000
      )
    }
  }

  useEffect(() => {
    startTimer()
    return () => {
      if (timer != null) {
        clearTimeout(timer)
      }
    }
  })

  return (
    <>
      {hasPagination && (
        <GridPaginationComponent
          currentPage={page}
          totalItems={data.length}
          pageSize={pageSize}
          onPageChange={handleChangePage}
          position='top'
        />
      )}
      {exportUrl && (
        <div className='text-end pb-4'>
          <Tooltip title='Export to Excel'>
            <Button
              onClick={(e) => {
                e.preventDefault()
                window.open(exportUrl, '_blank')
              }}
              variant='contained'
              color='success'
              className='text-white'
            >
              <i className='fa fa-file-excel text-white' />
              &nbsp;&nbsp;Download XLS
            </Button>
          </Tooltip>
        </div>
      )}
      {/* begin::Table container */}
      <div className='table-responsive'>
        {/* begin::Table */}
        <table className='table align-middle gs-0 gy-3 table-row-dashed table-row-gray-300'>
          {/* begin::Table head */}
          <thead>
            <tr className='fw-bold text-muted bg-light text-center'>
              {columns.map((column) => (
                <th
                  className={`min-w-100px ${!column.index ? 'ps-4 rounded-start' : ''} ${
                    column.index === columnsCount ? 'text-end rounded-end pe-4' : ''
                  }`}
                  key={column['field']}
                >
                  {column.caption}
                </th>
              ))}
              {controls.length > 0 && (
                <th className='min-w-100px rounded-end pe-4 text-center' key='grid-controls'>
                  Actions
                </th>
              )}
            </tr>
          </thead>
          {/* end::Table head */}
          {/* begin::Table body */}
          <tbody>
            {(data === undefined || data.length === 0) && (
              <tr>
                <th
                  colSpan={columns.length + (controls.length > 0 ? 1 : 0)}
                  className='text-center'
                >
                  {currentUpdateId == null ? <LoadingIndicator /> : noData}
                </th>
              </tr>
            )}
            {data !== undefined &&
              data.length > 0 &&
              data.slice(page * pageSize, page * pageSize + pageSize).map((row) => {
                const requestValues: {[key: string]: any} = {}

                requestValues[gridId] = {activeRecordId: row[keyField]}

                const rowStyle: {[key: string]: any} = {}
                if (row.color != null) {
                  rowStyle.background = row.color
                }

                return (
                  <tr key={row.id} style={rowStyle}>
                    {columns.map((column) => (
                      <GridColumnComponent
                        column={column}
                        row={row}
                        context={context}
                        additionalRequestData={requestValues}
                        className={row.color != null && mode === 'dark' ? 'text-black' : ''}
                      />
                    ))}
                    {controls.length > 0 && (
                      <td className='py-2 px-3'>
                        {controls.map((config) => {
                          if (config.type === 'PDF_VIEWER') {
                            return (
                              <PdfViewerGridButtonComponent
                                key={`grid-control-${row.id}-${config.id}`}
                                context={context}
                                config={config}
                                row={row}
                              />
                            )
                          }
                          return (
                            <ButtonComponent
                              key={`grid-control-${row.id}-${config.id}`}
                              context={context}
                              config={config}
                              values={requestValues}
                              className='my-2 mx-2'
                            />
                          )
                        })}
                      </td>
                    )}
                  </tr>
                )
              })}
          </tbody>
          {/* end::Table body */}
        </table>
      </div>
      {/* end::Table container */}
      {hasPagination && (
        <GridPaginationComponent
          currentPage={page}
          totalItems={data.length}
          pageSize={pager?.pageSize ?? 30}
          onPageChange={handleChangePage}
          position='bottom'
        />
      )}
    </>
  )
}
